import React, { useState } from 'react'
import PropTypes from 'prop-types'

import * as R from 'ramda'

import FormEditor from 'coact/editors/FormEditor'
import TableEditor from 'coact/editors/TableEditor'
import ProductReader from 'coact/components/ProductReader'
import PageActions from 'coact/pages/EditPage/PageActions'
import * as $editorSaga from 'coact/saga/editorSaga'
import createEffectAction from 'coact/pages/modules/pageAction/createEffectAction'
import Editor from 'coact/editors/Editor'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'

import InvoiceTable from '~/src/components/InvoiceTable/index.js'

import PaymentMethod from './PaymentMethod.jsx'
import fetchInvoiceStatus from './fetchInvoiceStatus.js'
import overspend from './overspend.js'
import returnProblems from './returnProblems.js'
import awardRows from './awardRows.js'
import { vendorSectionUi, isCreated } from './vendorSectionUi.js'

const { object, func, bool, array } = PropTypes

// Shown when the server reports it cannot issue an invoice. The staff decides:
// record the sale without one, or back out and leave the order untouched.
function NoInvoiceDialog(props) {
  const { open, onCancel, onContinue } = props

  return (
    <Dialog open={open} onClose={onCancel} aria-labelledby="no-invoice-title">
      <DialogTitle id="no-invoice-title">
        <span style={{ fontSize: 28 }}>目前無法開立發票</span>
      </DialogTitle>
      <DialogContent>
        <DialogContentText style={{ fontSize: 20 }}>
          發票服務尚未設定，這筆結帳不會開立發票。
        </DialogContentText>
        <DialogContentText style={{ fontSize: 20 }}>
          選「繼續結帳」會照常存下訂單；等發票服務恢復後，可以回到這筆訂單重新結帳補開。
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} size="large" style={{ fontSize: 20 }}>
          取消
        </Button>
        <Button
          onClick={onContinue}
          variant="contained"
          color="primary"
          size="large"
          style={{ fontSize: 20 }}
        >
          繼續結帳（不開發票）
        </Button>
      </DialogActions>
    </Dialog>
  )
}
NoInvoiceDialog.propTypes = {
  open: bool.isRequired,
  onCancel: func.isRequired,
  onContinue: func.isRequired,
}

// Shown when a redemption asks for more than the balance we last read.
//
// A WARNING, NOT A GATE. The balance is a snapshot and may be out of date, the
// staff can see the customer and we cannot, and blocking would mean refusing a
// sale over a loyalty figure. So it says what it knows and lets them through.
function OverspendDialog(props) {
  const { problems, onCancel, onContinue } = props

  return (
    <Dialog
      open={problems.length > 0}
      onClose={onCancel}
      aria-labelledby="overspend-title"
    >
      <DialogTitle id="overspend-title">
        <span style={{ fontSize: 28 }}>折抵超過會員餘額</span>
      </DialogTitle>
      <DialogContent>
        {problems.map(problem => (
          <DialogContentText key={problem.label} style={{ fontSize: 20 }}>
            {`${problem.label} ${problem.wanted}${problem.unit}，查到的餘額只有 ${problem.balance}${problem.unit}`}
          </DialogContentText>
        ))}
        <DialogContentText style={{ fontSize: 20 }}>
          餘額是查詢當時的數字，客人也可能在線上消費過。要繼續的話，SHOPLINE
          有可能會退回這筆折抵。
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} size="large" style={{ fontSize: 20 }}>
          回去修改
        </Button>
        <Button
          onClick={onContinue}
          variant="contained"
          color="primary"
          size="large"
          style={{ fontSize: 20 }}
        >
          繼續結帳
        </Button>
      </DialogActions>
    </Dialog>
  )
}
OverspendDialog.propTypes = {
  problems: array.isRequired,
  onCancel: func.isRequired,
  onContinue: func.isRequired,
}

// FormEditor renders its own <h3> for `title`, and a browser default h3 is far
// too loud and too tall for a label that sits above one row of fields. Scoped
// to the class so nothing else on the page is touched.
const SECTION_CSS = `
  .cashier-section {
    margin: 0 12px 8px 0;
    padding: 2px 10px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
  }
  .cashier-section h3 {
    margin: 2px 0 0;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.1em;
    color: rgba(0, 0, 0, 0.54);
  }
  .cashier-awards {
    border-collapse: collapse;
    margin: 4px 0 8px;
    font-size: 14px;
  }
  .cashier-awards th {
    text-align: left;
    font-weight: 500;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.54);
    padding: 0 16px 2px 0;
  }
  .cashier-awards td {
    padding: 2px 16px 2px 0;
    white-space: nowrap;
  }
  .cashier-awards td.amount {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .cashier-awards td.failed {
    color: #9a3b33;
  }
  /* A section may ask for emphasis by naming a class in ui.head. The figures
     in 金額資訊 are what the staff reads out loud, so they carry weight and
     colour that the fields around them do not.

     -webkit-text-fill-color as well as color, because 數量 and 金額 are
     disabled fields: WebKit greys a disabled input out through that property
     and would otherwise ignore the colour entirely. */
  .cashier-amounts input,
  .cashier-amounts input:disabled {
    color: #c62828;
    -webkit-text-fill-color: #c62828;
    font-weight: 700;
  }
`

// What a vendor section shows once the order exists.
//
// The three input boxes are gone by then, because an order's awards are decided
// when it is created and awardIntake drops anything a later save sends. Leaving
// editable boxes there would invite the staff to type into fields that silently
// do nothing.
//
// Read-only on purpose, and not merely because editing is unimplemented: every
// index in this array is half of a reference already written into the provider's
// history, so the array cannot be reordered or rewritten at all.
function AwardsTable(props) {
  const { awards } = props

  if (awards.length === 0) {
    return (
      <div style={{ padding: '4px 0 8px', color: 'rgba(0, 0, 0, 0.54)' }}>
        沒有點數異動
      </div>
    )
  }

  return (
    <table className="cashier-awards">
      <thead>
        <tr>
          <th>項目</th>
          <th>數量</th>
          <th>狀態</th>
          <th>備註</th>
        </tr>
      </thead>
      <tbody>
        {awards.map(award => (
          <tr key={award.index}>
            <td>{award.label}</td>
            <td className="amount">{award.amount.toLocaleString('zh-TW')}</td>
            <td className={award.status === '失敗' ? 'failed' : undefined}>
              {award.status}
            </td>
            <td>{award.error || award.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
AwardsTable.propTypes = {
  awards: array.isRequired,
}

// One vendor section, in whichever of its states applies. Which fields, and
// whether they may be typed into, is decided by vendorSectionUi -- see there
// for why a vendor we cannot name renders read-only rather than hidden.
function VendorSection(props) {
  const { section, schema } = props

  return (
    <Editor>
      {editor => {
        const doc = R.propOr({}, 'value', editor)
        const created = isCreated(doc)

        return (
          <React.Fragment>
            <FormEditor ui={vendorSectionUi(section, doc)} schema={schema} />
            {created ? <AwardsTable awards={awardRows(doc)} /> : null}
          </React.Fragment>
        )
      }}
    </Editor>
  )
}
VendorSection.propTypes = {
  section: object.isRequired,
  schema: object.isRequired,
}

// The head form used to be one nineteen-field grid, which meant reading the
// whole thing to find the one field you wanted. Same fields, same paths, same
// order within a group -- see ui.head for the grouping. Each entry there is a
// row, so a row holding two sections puts them side by side.
//
// A section declaring `savedFields` is a vendor section and has two states; the
// rest render the same whatever the order's state.
function HeadSections(props) {
  const { rows, schema } = props

  return (
    <React.Fragment>
      <style>{SECTION_CSS}</style>
      {rows.map(sections => (
        <div
          key={sections.map(s => s.title).join('/')}
          style={{ display: 'flex', alignItems: 'flex-start' }}
        >
          {sections.map(section => (
            <div
              key={section.title}
              className={
                section.className
                  ? `cashier-section ${section.className}`
                  : 'cashier-section'
              }
            >
              {section.savedFields ? (
                <VendorSection section={section} schema={schema} />
              ) : (
                <FormEditor ui={section} schema={schema} />
              )}
            </div>
          ))}
        </div>
      ))}
    </React.Fragment>
  )
}
HeadSections.propTypes = {
  rows: array.isRequired,
  schema: object.isRequired,
}

// Why a 退貨結帳 was refused.
//
// NO "CONTINUE ANYWAY", which is the difference between this and the
// over-balance dialog. That one guards a snapshot that is allowed to be stale;
// every one of these is a mistake -- a positive total is not a return, and a
// 折抵 on one would take points off a customer handing goods back.
function ReturnProblemDialog(props) {
  const { problems, onCancel } = props

  return (
    <Dialog
      open={problems.length > 0}
      onClose={onCancel}
      aria-labelledby="return-problem-title"
    >
      <DialogTitle id="return-problem-title">
        <span style={{ fontSize: 28 }}>這筆不能當退貨結帳</span>
      </DialogTitle>
      <DialogContent>
        {problems.map(problem => (
          <DialogContentText key={problem} style={{ fontSize: 20 }}>
            {problem}
          </DialogContentText>
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          variant="contained"
          color="primary"
          size="large"
          style={{ fontSize: 20 }}
        >
          回去修改
        </Button>
      </DialogActions>
    </Dialog>
  )
}
ReturnProblemDialog.propTypes = {
  problems: array.isRequired,
  onCancel: func.isRequired,
}

function Cashier(props) {
  const {
    page: { schema, ui },
    dispatch,
    actions,
  } = props

  // setTimeout(() => (
  //   dispatch($message.act.send(
  //     { type: 'error', message: '預計開立發票 VV12345678 已存在，不可重複開立，請聯絡管理人員檢查修正' },
  //   ))
  // ), 5000)

  // `run: 'first'` for the same reason as `checkout` below: this effect ends in
  // a save too, and 作廢 is not an operation to run twice.
  const invalidInvoice = id =>
    dispatch(createEffectAction({ run: 'first', name: 'invalidInvoice', id }))

  const [isAsking, setIsAsking] = useState(false)
  const [noInvoice, setNoInvoice] = useState(false)
  const [overspending, setOverspending] = useState([])
  const [badReturn, setBadReturn] = useState([])

  // `run: 'first'` IS THE DOUBLE-CHECKOUT GUARD. takeFirst takes one action and
  // then BLOCKS on it (coact/saga/utils), so a second press anywhere in the
  // save, the issue or the print is dropped -- and that is the whole window.
  //
  // Without it the effect defaults to `run: 'every'`, and two presses ran two
  // sagas. The second saved before the first had answered, so `page.id` was
  // still unset and the save was a CREATE -- a second order carrying its own
  // 統一發票, its own SHOPLINE 點數 and its own stock movement. Seen in
  // production: RO260927007 and RO260927008, one cart, 340ms apart.
  //
  // NOT A `disabled` PROP, which is where this looks like it belongs. Nothing
  // tells this component the saga ended -- the dispatch is fire and forget,
  // and coact's edit phase latches at `dataSaving`, saveDataTask putting
  // `dataLoaded` on the success path only. A button disabled on either signal
  // stays dead after the first validation error, and the way out of that is a
  // reload, which loses the cart. `isAsking` is left as it was for the same
  // reason: it covers the status fetch and it always clears.
  //
  // coact's own 儲存 and 刪除 declare this already; see
  // pages/EditPage/builtinActions.
  const checkout = extra =>
    dispatch(
      createEffectAction({
        run: 'first',
        name: 'saveAndPrintInvoice',
        ...extra,
      }),
    )

  // Asked before the save, not after: the answer decides whether the order is
  // written at all, and a staff member who cancels should be left with the
  // order still on screen.
  const askInvoiceThenCheckout = async () => {
    setIsAsking(true)
    try {
      const { configured } = await fetchInvoiceStatus()

      if (configured) {
        checkout()

        return
      }

      setNoInvoice(true)
    } finally {
      setIsAsking(false)
    }
  }

  // The balance question comes first, and at the button rather than while the
  // staff types -- a dialog that appeared on the way to "500" would fire at "5".
  const onCheckout = doc => {
    const problems = overspend(doc)

    if (problems.length) {
      setOverspending(problems)

      return
    }

    askInvoiceThenCheckout()
  }

  // A return is saved like any other order and issues NOTHING: a 統一發票 is not
  // cancelled by writing another one, so the credit note is handled outside
  // this system. `skipInvoice` is the same path the staff takes when the
  // invoice service is down, which already records an order and issues nothing.
  // 退貨結帳 sits between the saves and 刪除, which means splitting the action
  // list rather than appending to it: a page action declared in pageConfig is
  // merged AFTER the builtins (EditPage/index.js), so it could only ever land
  // to the right of 刪除.
  //
  // `withActions` has already dropped anything this user may not do, so when
  // 刪除 is not theirs to press `removeAction` is empty and 退貨結帳 is simply
  // last -- no placeholder, no gap.
  const beforeRemove = actions.filter(action => action.key !== 'remove')
  const removeAction = actions.filter(action => action.key === 'remove')

  const onReturnCheckout = doc => {
    const problems = returnProblems(doc)

    if (problems.length) {
      setBadReturn(problems)

      return
    }

    checkout({ skipInvoice: true })
  }

  return (
    <React.Fragment>
      <link
        href="https://fonts.googleapis.com/css?family=Libre+Barcode+39&display=swap"
        rel="stylesheet"
      />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <PageActions {...props} actions={beforeRemove} />
        <Editor>
          {editor => (
            <Button
              variant="contained"
              color="secondary"
              disabled={isAsking}
              onClick={() => onReturnCheckout(editor.value)}
              style={{ marginLeft: 8 }}
            >
              退貨結帳
            </Button>
          )}
        </Editor>
        <PageActions {...props} actions={removeAction} />
      </div>
      <div style={{ display: 'inline-block' }}>
        <HeadSections rows={ui.head} schema={schema} />
        <div
          style={{
            float: 'right',
            display: 'flex',
            marginTop: 10,
            alignItems: 'baseline',
            justifyContent: 'space-between',
            width: 900,
          }}
        >
          <Editor>
            {editor => (
              <PaymentMethod
                value={editor.value?.payType}
                onChange={e => {
                  dispatch(
                    $editorSaga.act.editPath({
                      path: ['payType'],
                      value: e.target.value,
                    }),
                  )
                }}
              />
            )}
          </Editor>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 200, marginLeft: 10 }}>
              <Editor>
                {editor => (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isAsking}
                    onClick={() => onCheckout(editor.value)}
                    style={{
                      height: 60,
                      width: 200,
                      fontSize: 30,
                    }}
                  >
                    結帳開發票
                  </Button>
                )}
              </Editor>
              {/*
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  dispatch(createEffectAction({ name: 'saveAndPrintInvoiceOnPos' }))
                }}
                style={{
                  marginTop: 10,
                  height: 60,
                  width: 200,
                  fontSize: 30,
                }}
              >
                列印
              </Button>
              */}
            </div>
          </div>
        </div>
        <ProductReader {...ui.productReader} />
        <TableEditor ui={ui.items} schema={schema} />
        <InvoiceTable ui={ui.invoices} invalidInvoice={invalidInvoice} />
        <div
          style={{
            fontFamily: "'Libre Barcode 39', cursive",
            fontSize: 1,
            color: 'white',
          }}
        >
          *font*
        </div>
        <div>
          <img
            style={{ width: 1, height: 1 }}
            src="invoiceLogo.png"
            alt="禾宏文化"
          />
        </div>
      </div>
      <OverspendDialog
        problems={overspending}
        onCancel={() => setOverspending([])}
        onContinue={() => {
          setOverspending([])
          askInvoiceThenCheckout()
        }}
      />
      <ReturnProblemDialog
        problems={badReturn}
        onCancel={() => setBadReturn([])}
      />
      <NoInvoiceDialog
        open={noInvoice}
        onCancel={() => setNoInvoice(false)}
        onContinue={() => {
          setNoInvoice(false)
          checkout({ skipInvoice: true })
        }}
      />
    </React.Fragment>
  )
}

Cashier.propTypes = {
  page: object.isRequired,
  dispatch: func.isRequired,
  actions: array.isRequired,
}

export default Cashier
